import React, { useCallback, useState, useEffect, useMemo } from 'react';
// Simple test change to verify editor functionality
import Table from '../../components/Table/Table'; 
import PlaygroundSettingsBar from '../../components/PlaygroundSettingsBar/PlaygroundSettingsBar';
// Import CardPicker and related types/constants from BuddyPage
import { CardPicker, CardPickerProps, RANKS, SUITS } from '../../app/BuddyPage';
// Import Hand type
import { Hand } from '../../app/types'; 
// REMOVE Import Position type
// import { Position } from '../../app/types'; 
// Import the BetAmountInput component
import BetAmountInput from '../../components/BetAmountInput/BetAmountInput';
import './PlaygroundPage.css';

// Define ActionType here if needed by Table/PokerVisual later
export type ActionType = 'fold' | 'call' | 'raise' | 'check' | 'bet' | 'straddle';
// Use unique local name for Position type and EXPORT it
export type PlaygroundPosition = 'BTN' | 'SB' | 'BB' | 'UTG' | 'UTG+1' | 'UTG+2' | 'MP' | 'LJ' | 'HJ' | 'CO';
// Define Street type (lowercase) and EXPORT it
export type Street = 'preflop' | 'flop' | 'turn' | 'river';
// Define Action Object Type
interface Action {
  type: ActionType;
  amount?: number;
  isAllIn?: boolean;
  // Add player info if needed later for history display
  // playerId: number;
}

// <<< ADD SUIT_SYMBOLS constant >>>
const SUIT_SYMBOLS: { [key: string]: string } = {c:'♣', h:'♥', d:'♦', s:'♠'};

// --- Move Helper Functions & Constants OUTSIDE Component ---
const STREET_ORDER: Street[] = ['preflop', 'flop', 'turn', 'river'];

const getOrderedPlayers = (street: Street, currentSeats: { id: number; label: string; }[]): { id: number; label: string; }[] => {
  const positionOrder = street === 'preflop'
      ? ['UTG', 'UTG+1', 'UTG+2', 'MP', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      : ['SB', 'BB', 'UTG', 'UTG+1', 'UTG+2', 'MP', 'LJ', 'HJ', 'CO', 'BTN'];

  return [...currentSeats].sort((a, b) => {
    // Use PlaygroundPosition for casting here
    const posAIndex = positionOrder.indexOf(a.label as PlaygroundPosition);
    const posBIndex = positionOrder.indexOf(b.label as PlaygroundPosition);
    return posAIndex - posBIndex;
  });
};
// --- End Moved Functions ---

// Define seats configuration outside the component for stability
const seats9Max = [
  { id: 0, label: 'BTN' as PlaygroundPosition }, { id: 1, label: 'SB' as PlaygroundPosition }, { id: 2, label: 'BB' as PlaygroundPosition },
  { id: 3, label: 'UTG' as PlaygroundPosition }, { id: 4, label: 'UTG+1' as PlaygroundPosition }, { id: 5, label: 'UTG+2' as PlaygroundPosition },
  { id: 6, label: 'MP' as PlaygroundPosition }, { id: 7, label: 'LJ' as PlaygroundPosition }, { id: 8, label: 'CO' as PlaygroundPosition },
];

const PlaygroundPage: React.FC = () => {
  const [heroSeatId, setHeroSeatId] = useState<number | null>(null); // Actual ID of hero
  // State for the *position* selected in the setup bar - Use renamed type
  const [selectedHeroPosition, setSelectedHeroPosition] = useState<PlaygroundPosition | null>(null);
  const [isSetupComplete, setIsSetupComplete] = useState<boolean>(false);
  // --- Add SB/BB State --- 
  const [smallBlind, setSmallBlind] = useState<number>(1);
  const [bigBlind, setBigBlind] = useState<number>(2);
  // --- Add Hero Card & Picker State --- 
  const [heroCards, setHeroCards] = useState<{ holeCard1: string; holeCard2: string }>({ holeCard1: '', holeCard2: '' });
  const [showCardPicker, setShowCardPicker] = useState<boolean>(false);
  const [cardPickerTarget, setCardPickerTarget] = useState<'holeCard1' | 'holeCard2' | 'flopCard1' | 'flopCard2' | 'flopCard3' | 'turnCard' | 'riverCard' | null>(null);
  
  // --- Add Community Cards State ---
  const [communityCards, setCommunityCards] = useState<{
    flopCard1: string;
    flopCard2: string;
    flopCard3: string;
    turnCard: string;
    riverCard: string;
  }>({
    flopCard1: '',
    flopCard2: '',
    flopCard3: '',
    turnCard: '',
    riverCard: ''
  });
  
  // --- Add Stack Sizes State --- 
  const [stackSizes, setStackSizes] = useState<Record<number, number>>({});
  // --- Add Current Street State --- 
  const [currentStreet, setCurrentStreet] = useState<Street>('preflop');
  // --- Add Pot State --- 
  const [pot, setPot] = useState<number>(0);
  // --- Add Action State ---
  const [playerActions, setPlayerActions] = useState<Record<number, Partial<Record<Street, Action[]>>>>({});
  const [currentPlayerId, setCurrentPlayerId] = useState<number | null>(null);
  const [bets, setBets] = useState<Partial<Record<Street, number>>>({});
  const [foldedPlayerIds, setFoldedPlayerIds] = useState<Set<number>>(new Set());
  const [minBetAmount, setMinBetAmount] = useState<number>(0);
  const [showBetInput, setShowBetInput] = useState<boolean>(false);
  const [betInputActionTarget, setBetInputActionTarget] = useState<{ seatId: number; actionType: ActionType } | null>(null);
  const [currentStackForBetInput, setCurrentStackForBetInput] = useState<number>(0);
  // --- Add player street contributions state ---
  const [playerStreetContributions, setPlayerStreetContributions] = useState<Record<number, number>>({});
  // <<< ADDED: State to track the next player ID >>>
  const [nextPlayerToActId, setNextPlayerToActId] = useState<number | null>(null);
  const [isDebugSwivelMode, setIsDebugSwivelMode] = useState(false); // State for debug swivel mode

  // --- Helper Functions (Moved BEFORE useEffect hooks) ---
  // const STREET_ORDER: Street[] = ['preflop', 'flop', 'turn', 'river']; // MOVED OUTSIDE

  /* MOVED OUTSIDE
  const getOrderedPlayers = useCallback((street: Street, currentSeats: typeof seats9Max): typeof seats9Max => {
    const positionOrder = street === 'preflop'
        ? ['UTG', 'UTG+1', 'UTG+2', 'MP', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB']
        : ['SB', 'BB', 'UTG', 'UTG+1', 'UTG+2', 'MP', 'LJ', 'HJ', 'CO', 'BTN'];

    return [...currentSeats].sort((a, b) => {
      // Use PlaygroundPosition for casting here
      const posAIndex = positionOrder.indexOf(a.label as PlaygroundPosition);
      const posBIndex = positionOrder.indexOf(b.label as PlaygroundPosition);
      return posAIndex - posBIndex;
    });
  }, []);
  */

  // --- Calculate Next Player Logic (Adapted from BuddyPage) ---
  const calculateNextPlayerToAct = useCallback((
    actingPlayerId: number | null,
    actionTaken: Action | null, // We might not use actionTaken directly, rely on updated currentActions
    currentStreetState: Street,
    currentPlayers: typeof seats9Max, // Represents the full list of seats/potential players
    currentActions: typeof playerActions, // The latest actions state *including* the action just taken
    currentBets: typeof bets, // Bets state *before* this action (handleAction updates based on return)
    currentFoldedIds: typeof foldedPlayerIds // Folded state *including* the fold just taken if applicable
  ): { nextPlayerId: number | null, nextStreet: Street, nextBets: typeof bets, nextMinBet: number, streetContributions: Record<number, number> } => {
    console.log(`[calculateNextPlayer V2] START: Street=${currentStreetState}, LastActed=${actingPlayerId}, Action=${actionTaken?.type}`);

    // === Helper Functions ===
    const getPlayerActions = (playerId: number, street: Street): Action[] => {
      return currentActions[playerId]?.[street] || [];
    };

    // Checks if player folded *before* the given street started
    const hasPlayerFoldedBefore = (playerId: number, street: Street): boolean => {
      const streetIndex = STREET_ORDER.indexOf(street);
      return STREET_ORDER.slice(0, streetIndex).some(s =>
        getPlayerActions(playerId, s).some(a => a.type === 'fold')
      );
    };

    // Checks if player folded *on or before* the given street
    const hasPlayerFoldedOnOrBefore = (playerId: number, street: Street): boolean => {
      const streetIndex = STREET_ORDER.indexOf(street);
      return STREET_ORDER.slice(0, streetIndex + 1).some(s =>
        getPlayerActions(playerId, s).some(a => a.type === 'fold')
      );
    };
    // --- TODO: Add isPlayerAllIn check later if needed ---


    // === 1. Handle Start of Street ===
    if (actingPlayerId === null) {
      // Find players not folded *before* this street
      const activePlayersAtStart = currentPlayers.filter(p => !hasPlayerFoldedBefore(p.id, currentStreetState));
      if (activePlayersAtStart.length <= 1) {
        console.log(`[calculateNextPlayer/Start] 0 or 1 players left at start of ${currentStreetState}. Ending hand/street early.`);
        // If only 1 player remains overall, the hand should end. If >1 overall but only 1 for this street, skip betting.
        // Returning null signals the end of betting for this street. The caller (handleAction) might need more logic later.
        return { nextPlayerId: null, nextStreet: currentStreetState, nextBets: currentBets, nextMinBet: 0, streetContributions: {} };
      }
      const orderedPlayersAtStart = getOrderedPlayers(currentStreetState, activePlayersAtStart);
      const firstPlayer = orderedPlayersAtStart[0];
      console.log(`[calculateNextPlayer/Start] First player on ${currentStreetState}: P${firstPlayer?.id} (${firstPlayer?.label})`);
      // Reset bets for the new street (except preflop, where blinds are assumed)
      const nextBetsForStreetStart = currentStreetState === 'preflop' ? currentBets : { ...currentBets, [currentStreetState]: 0 };
      
      // For preflop, initialize street contributions with blinds
      const initialStreetContributions: Record<number, number> = {};
      if (currentStreetState === 'preflop') {
        const sbPlayer = currentPlayers.find(p => p.label === 'SB');
        const bbPlayer = currentPlayers.find(p => p.label === 'BB');
        if (sbPlayer && !hasPlayerFoldedBefore(sbPlayer.id, currentStreetState)) {
          initialStreetContributions[sbPlayer.id] = smallBlind;
        }
        if (bbPlayer && !hasPlayerFoldedBefore(bbPlayer.id, currentStreetState)) {
          initialStreetContributions[bbPlayer.id] = bigBlind;
        }
      }
      
      return {
        nextPlayerId: firstPlayer ? firstPlayer.id : null,
        nextStreet: currentStreetState,
        nextBets: nextBetsForStreetStart,
        nextMinBet: 0,
        streetContributions: initialStreetContributions
      };
    }

    // === 2. Determine Players Still In Hand for this Street's Action ===
    // Players who haven't folded on this street OR any previous street.
    // This uses currentFoldedIds which includes the fold action if it just happened.
    const playersInHand = currentPlayers.filter(p => !currentFoldedIds.has(p.id));
    const orderedPlayersInHand = getOrderedPlayers(currentStreetState, playersInHand);
    const numActivePlayers = orderedPlayersInHand.length;

    console.log(`[calculateNextPlayer V2] Street: ${currentStreetState}, Players In Hand (not folded): ${orderedPlayersInHand.map(p => `P${p.id}(${p.label})`)}`);

    if (numActivePlayers <= 1) {
      console.log(`[calculateNextPlayer V2] Only ${numActivePlayers} player(s) left in hand after action P${actingPlayerId} ${actionTaken?.type}. Betting round/hand ends.`);
      // If betting ends, determine if we go to next street or end hand
      const currentStreetIndex = STREET_ORDER.indexOf(currentStreetState);
      const nextStreet = currentStreetIndex < STREET_ORDER.length - 1
                         ? STREET_ORDER[currentStreetIndex + 1]
                         : currentStreetState; // Stay on river if river ended
      console.log(`[calculateNextPlayer V2] Round End. Determining next street: ${nextStreet} (current: ${currentStreetState})`);

      // Decide if the *next* street should start or if hand is over
      let nextPlayerForNextStreet: number | null = null;
      let finalNextStreet = nextStreet;
      if (nextStreet !== currentStreetState) {
          const playersForNextStreet = currentPlayers.filter(p => !currentFoldedIds.has(p.id));
          if (playersForNextStreet.length > 1) {
              const orderedNext = getOrderedPlayers(nextStreet, playersForNextStreet);
              nextPlayerForNextStreet = orderedNext[0]?.id ?? null;
               console.log(`[calculateNextPlayer V2] Proceeding to ${nextStreet}. First player: P${nextPlayerForNextStreet}`);
          } else {
              console.log(`[calculateNextPlayer V2] Hand ends, only ${playersForNextStreet.length} player(s) for ${nextStreet}.`);
              finalNextStreet = currentStreetState; // Stay on current street visually if hand ends
          }
      } else {
         console.log(`[calculateNextPlayer V2] River betting ended.`);
      }
      // Reset bets if moving to a new street
      const finalBets = finalNextStreet !== currentStreetState ? { ...currentBets, [finalNextStreet]: 0 } : currentBets;

      console.log(`[calculateNextPlayer V2] Round over. Returning: Next Player=${nextPlayerForNextStreet}, Next Street=${finalNextStreet}`);
      return { 
        nextPlayerId: nextPlayerForNextStreet, 
        nextStreet: finalNextStreet, 
        nextBets: finalBets, 
        nextMinBet: 0,
        streetContributions: {} // Reset contributions for simplicity when round is over
      };
    }

    // === 3. Recalculate Street State (Contributions, Max Bet) ===
    // This needs to reflect the state *after* the actionTaken has been recorded in currentActions.
    let currentMaxContribution = 0; // Max amount contributed by any *single* player *on this street*
    let aggressorIdOnStreet: number | null = null; // Player who made the last bet/raise ON THIS STREET
    const streetContributions: Record<number, number> = {}; // Tracks total contribution per player *on this street*
    const lastActionIndexMap: Record<number, number> = {}; // Tracks the sequence index of the last action for each player
    let lastAggressionSequenceIndex = -1; // Sequence index of the last bet/raise
    let bettingOpened = false; // Has a bet or raise occurred this street?
    const streetActionSequence: {playerId: number, action: Action, seqIndex: number}[] = [];
    let globalActionCounter = 0; // Simple sequence index
    let firstPlayerToActionThisStreetId: number | null = null; // ID of the first player eligible to act

    // Initialize contributions for blinds preflop
    if (currentStreetState === 'preflop') {
      const sbPlayer = currentPlayers.find(p => p.label === 'SB');
      const bbPlayer = currentPlayers.find(p => p.label === 'BB');
      if (sbPlayer && !currentFoldedIds.has(sbPlayer.id)) { // Only count if not folded
        streetContributions[sbPlayer.id] = smallBlind;
      }
      if (bbPlayer && !currentFoldedIds.has(bbPlayer.id)) { // Only count if not folded
        // Ensure BB contribution is at least BB amount (handles HU case where SB=BB)
        streetContributions[bbPlayer.id] = Math.max(streetContributions[bbPlayer.id] || 0, bigBlind);
      }
      currentMaxContribution = bigBlind; // Initial max contribution is BB
      bettingOpened = true; // Preflop betting is open by default due to blinds
      aggressorIdOnStreet = bbPlayer?.id ?? null; // BB is the initial 'aggressor'
      console.log(`[calculateNextPlayer V2] Preflop Init: SB Contrib=${streetContributions[sbPlayer?.id ?? -1]}, BB Contrib=${streetContributions[bbPlayer?.id ?? -1]}, MaxContrib=${currentMaxContribution}`);
    }

    // --- Build the chronological action sequence for THIS street ---
    // Use playersInHand to determine max actions *among those still active*
    const maxActionCountPerPlayer = Math.max(0, ...playersInHand.map(p => getPlayerActions(p.id, currentStreetState).length));

    for (let round = 0; round < maxActionCountPerPlayer; round++) {
      // Iterate using the positionally correct order for the street
      const orderedPlayersForRound = getOrderedPlayers(currentStreetState, currentPlayers); // Use original list to find actions positionally
      orderedPlayersForRound.forEach(player => {
        // Only consider actions from players still in the hand (not folded)
        if (!playersInHand.some(p => p.id === player.id)) return;

        const action = getPlayerActions(player.id, currentStreetState)?.[round];
        if (action) {
           // Record the first player who acted *among those still in the hand*
           if (firstPlayerToActionThisStreetId === null) {
               firstPlayerToActionThisStreetId = player.id;
           }
          const actionIndexInSequence = globalActionCounter++;
          streetActionSequence.push({ playerId: player.id, action: action, seqIndex: actionIndexInSequence });
        }
      });
    }
    console.log(`[calculateNextPlayer V2] Built action sequence for ${currentStreetState}:`, streetActionSequence.map(a => `P${a.playerId}(${currentPlayers.find(p=>p.id===a.playerId)?.label}) ${a.action.type} ${a.action.amount ?? ''}`));

    // --- Iterate sequence to calculate contributions & find aggressor ---
    streetActionSequence.forEach(({ playerId, action, seqIndex }) => {
        let playerCurrentTotalContributionThisStreet = streetContributions[playerId] || 0;

        if (action.type === 'bet' || action.type === 'raise') {
            // Action amount IS the new total contribution level for this street
            const newTotalContribution = action.amount || 0;
            // Amount added is difference from previous contribution *this street*
            const amountAdded = newTotalContribution - playerCurrentTotalContributionThisStreet;
            streetContributions[playerId] = newTotalContribution;
            currentMaxContribution = Math.max(currentMaxContribution, newTotalContribution);
            aggressorIdOnStreet = playerId;
            lastAggressionSequenceIndex = seqIndex;
            bettingOpened = true;
            console.log(`  P${playerId} ${action.type} ${action.amount}. Added: ${amountAdded}. Contrib now: ${newTotalContribution}, New Max: ${currentMaxContribution}`);
        } else if (action.type === 'call') {
            // Player calls the current maximum contribution level.
            // Their new total contribution *matches* that level.
            // We need the max contribution *before* this call happened.
            const maxContributionBeforeThisCall = Object.values(streetContributions)
                                                     .reduce((max, contrib) => Math.max(max, contrib), (currentStreetState === 'preflop' ? bigBlind : 0));

            const amountAdded = maxContributionBeforeThisCall - playerCurrentTotalContributionThisStreet;
            streetContributions[playerId] = maxContributionBeforeThisCall; // Their contribution now matches the level they called
            console.log(`  P${playerId} calls. Added: ${amountAdded}. Matches level: ${maxContributionBeforeThisCall}. Contrib now: ${streetContributions[playerId]}`);
            // Max contribution level doesn't change on a call relative to what it was before the call
        } else if (action.type === 'check') {
             console.log(`  P${playerId} checks. Contrib remains: ${playerCurrentTotalContributionThisStreet}`);
             // If BB checks preflop, they become the aggressor conceptually (last to close action)
             if (currentStreetState === 'preflop' && currentPlayers.find(p => p.id === playerId)?.label === 'BB' && currentMaxContribution === bigBlind) {
                 aggressorIdOnStreet = playerId; // BB check closes action if no raise
                 lastAggressionSequenceIndex = seqIndex;
                 console.log(`    BB Check detected, closing action. Aggressor set to P${playerId}`);
             }
        } else if (action.type === 'fold') {
            console.log(`  P${playerId} folds. Contrib remains: ${playerCurrentTotalContributionThisStreet}`);
            // Contribution doesn't change, player is removed from playersInHand logic flow later
        }

        lastActionIndexMap[playerId] = seqIndex; // Track last action index for this player
    });

    // Recalculate the true maximum contribution based on final streetContributions
    const finalMaxContributionOnStreet = Math.max(0, ...Object.values(streetContributions));

    console.log(`[calculateNextPlayer V2] Recalc End: Street=${currentStreetState}, FinalMaxContrib=${finalMaxContributionOnStreet}, Aggressor=${aggressorIdOnStreet}, BettingOpened=${bettingOpened}, FirstActor=${firstPlayerToActionThisStreetId}`);
    console.log("[calculateNextPlayer V2] Final Contributions: ", streetContributions);

    // === 4. Check End of Round Conditions ===
    let roundOver = false;

    // Condition 1: Enough actions taken (at least one per active player who hasn't folded)
    // AND action closed (last actor is before aggressor OR BB check closes preflop)
    if (streetActionSequence.length >= numActivePlayers) {
      const allActivePlayersMatchedOrFolded = orderedPlayersInHand.every(p => {
        const contribution = streetContributions[p.id] || 0;
        // All-in check would go here too
        return contribution === finalMaxContributionOnStreet;
      });

      let actionClosed = false;
      const lastAction = streetActionSequence[streetActionSequence.length - 1];
      const lastActorId = lastAction?.playerId;

      // Check if last action was by player before aggressor OR BB check closing action
      if (aggressorIdOnStreet !== null && lastActorId !== null) {
          // Special case for preflop: BB gets to act if everyone just calls
          if (currentStreetState === 'preflop' && bettingOpened && finalMaxContributionOnStreet === bigBlind) {
              // Find BB player
              const bbPlayer = currentPlayers.find(p => p.label === 'BB');
              if (bbPlayer) {
                  // Check if all other players have acted
                  const allOtherPlayersHaveActed = orderedPlayersInHand
                      .filter(p => p.id !== bbPlayer.id) // exclude BB
                      .every(p => streetActionSequence.some(a => a.playerId === p.id)); // check if each has acted
                  
                  // If BB hasn't acted yet AND all others have acted, they should get a chance
                  const bbHasActed = streetActionSequence.some(a => a.playerId === bbPlayer.id);
                  if (!bbHasActed && allOtherPlayersHaveActed && !currentFoldedIds.has(bbPlayer.id)) {
                      // BB still needs to act - continue the round
                      actionClosed = false;
                      console.log(`[calculateNextPlayer V2/EndCheck] BB (P${bbPlayer.id}) needs to act - continuing round`);
                  } else {
                      // BB has acted or is folded - can close the action
                      actionClosed = true;
                      console.log(`[calculateNextPlayer V2/EndCheck] BB (P${bbPlayer.id}) has acted - can close action`);
                  }
              }
          } 
          else {
              const aggressorIndex = orderedPlayersInHand.findIndex(p => p.id === aggressorIdOnStreet);
              if (aggressorIndex !== -1) {
                  const playerBeforeAggressorIndex = (aggressorIndex - 1 + numActivePlayers) % numActivePlayers;
                  const playerBeforeAggressorId = orderedPlayersInHand[playerBeforeAggressorIndex].id;

                  // Check if the last action was indeed by the player before the aggressor
                  // AND that last action was NOT a bet/raise itself (e.g. call closes, check closes)
                  if (lastActorId === playerBeforeAggressorId && lastAction && !['bet', 'raise'].includes(lastAction.action.type)) {
                      actionClosed = true;
                      console.log(`[calculateNextPlayer V2/EndCheck] Action closed by P${lastActorId} (player before aggressor P${aggressorIdOnStreet}).`);
                  }
              }
          }
      }

      if (allActivePlayersMatchedOrFolded && actionClosed) {
          console.log(`[calculateNextPlayer V2/EndCheck] Betting opened, all matched (${finalMaxContributionOnStreet}), and action closed. Round over.`);
          roundOver = true;
      } else if (!bettingOpened && streetActionSequence.length >= numActivePlayers) {
           console.log(`[calculateNextPlayer V2/EndCheck] Check-around complete. Round over.`);
           roundOver = true; // Check-around case
      }
       else {
           console.log(`[calculateNextPlayer V2/EndCheck] Round continues. AllMatched=${allActivePlayersMatchedOrFolded}, ActionClosed=${actionClosed}, Actions=${streetActionSequence.length}/${numActivePlayers}`);
      }
    } else {
        console.log(`[calculateNextPlayer V2/EndCheck] Round continues: Only ${streetActionSequence.length} actions taken, need at least ${numActivePlayers}.`);
    }

    // --- Handle Round Over ---
    if (roundOver) {
      console.log(`---> Round END detected on ${currentStreetState} <---`);
      const currentStreetIndex = STREET_ORDER.indexOf(currentStreetState);
      const nextStreet = currentStreetIndex < STREET_ORDER.length - 1
                         ? STREET_ORDER[currentStreetIndex + 1]
                         : currentStreetState; // Stay on river if river ended

      let firstPlayerNextStreet: number | null = null;
      let finalNextStreet = nextStreet;
      let finalBets = { ...currentBets, [currentStreetState]: finalMaxContributionOnStreet }; // Update current street's final bet

      if (finalNextStreet !== currentStreetState) {
          // Players for next street are those who didn't fold up to the END of the current one
          const playersForNextStreet = currentPlayers.filter(p => !hasPlayerFoldedOnOrBefore(p.id, currentStreetState));
          if (playersForNextStreet.length > 1) {
              const orderedNextStreet = getOrderedPlayers(finalNextStreet, playersForNextStreet);
              firstPlayerNextStreet = orderedNextStreet[0]?.id ?? null;
              finalBets = { ...finalBets, [finalNextStreet]: 0 }; // Reset next street bet to 0
               console.log(`[calculateNextPlayer V2] Proceeding to ${finalNextStreet}. First player: P${firstPlayerNextStreet}`);
          } else {
              console.log(`[calculateNextPlayer V2] Hand ends, only ${playersForNextStreet.length} player(s) for ${finalNextStreet}.`);
              finalNextStreet = currentStreetState; // Stay on current street visually if hand ends
          }
      } else {
         console.log(`[calculateNextPlayer V2] River betting ended.`);
      }

      console.log(`[calculateNextPlayer V2] Round over. Returning: Next Player=${firstPlayerNextStreet}, Next Street=${finalNextStreet}`);
      return { 
        nextPlayerId: firstPlayerNextStreet, 
        nextStreet: finalNextStreet, 
        nextBets: finalBets, 
        nextMinBet: 0,
        streetContributions: {} // Reset contributions for simplicity when round is over
      };
    }

    // === 5. Find Next Player To Act ===
    // Start searching from the player *after* the one who just acted, using the FULL positional order.
    const fullOrderedList = getOrderedPlayers(currentStreetState, currentPlayers);
    const lastActedIndexInFullList = fullOrderedList.findIndex(p => p.id === actingPlayerId);

    if (lastActedIndexInFullList === -1 && actingPlayerId !== null) {
      // This shouldn't happen if actingPlayerId was valid, but handle defensively.
      console.error(`[calculateNextPlayer V2] Error: Last acting player P${actingPlayerId} not found in the full player list!`);
      return { nextPlayerId: null, nextStreet: currentStreetState, nextBets: currentBets, nextMinBet: 0, streetContributions: {} }; // Indicate error/end
    }

    // Special case for preflop: if everyone has called (no raises) and BB hasn't acted yet
    if (currentStreetState === 'preflop' && bettingOpened && finalMaxContributionOnStreet === bigBlind) {
      const bbPlayer = currentPlayers.find(p => p.label === 'BB');
      if (bbPlayer && !currentFoldedIds.has(bbPlayer.id)) {
        const bbHasActed = Object.keys(streetContributions).length > 0 && 
          streetActionSequence.some(a => a.playerId === bbPlayer.id);
        
        // Only trigger this special case if all other players have acted 
        // (at least one action from each active player except BB)
        const allOtherPlayersHaveActed = orderedPlayersInHand
          .filter(p => p.id !== bbPlayer.id) // exclude BB
          .every(p => streetActionSequence.some(a => a.playerId === p.id)); // check if each has acted
        
        if (!bbHasActed && allOtherPlayersHaveActed) {
          // BB hasn't acted yet, all others have, and should be next
          console.log(`[calculateNextPlayer V2] Preflop special case: BB (P${bbPlayer.id}) hasn't acted yet and should be next`);
          return {
            nextPlayerId: bbPlayer.id,
            nextStreet: currentStreetState,
            nextBets: currentBets,
            nextMinBet: 0,
            streetContributions
          };
        }
      }
    }

    // Determine the starting index for the search in the full list
    const searchStartIndexInFullList = (lastActedIndexInFullList + 1) % fullOrderedList.length;

    for (let i = 0; i < fullOrderedList.length; i++) { // Loop through the full list length
        const potentialPlayerIndexInFullList = (searchStartIndexInFullList + i) % fullOrderedList.length;
        const potentialPlayer = fullOrderedList[potentialPlayerIndexInFullList];

        // IMPORTANT: Check if this player is actually still in the hand (not folded)
        if (!orderedPlayersInHand.some(p => p.id === potentialPlayer.id)) {
           console.log(`[FindNext V2] Skipping P${potentialPlayer.id} (${potentialPlayer.label}) as they have folded.`);
           continue; // Skip folded players
        }

        // Now apply the logic only to players still in the hand
        // TODO: Skip if player is all-in

        // Check if this player needs to act based on contributions
        const playerContribution = streetContributions[potentialPlayer.id] || 0;

        if (playerContribution < finalMaxContributionOnStreet) {
            // This player has contributed less than the maximum bet/raise on the street
            console.log(`[FindNext V2] P${potentialPlayer.id} (${potentialPlayer.label}) needs to act (Contrib ${playerContribution} < Max ${finalMaxContributionOnStreet})`);
            // Update the bets state to reflect the current amount needed to call
            const nextBets = { ...currentBets, [currentStreetState]: finalMaxContributionOnStreet };
            return { 
              nextPlayerId: potentialPlayer.id, 
              nextStreet: currentStreetState, 
              nextBets: nextBets, 
              nextMinBet: 0,
              streetContributions 
            };
        } else if (playerContribution === finalMaxContributionOnStreet && bettingOpened) {
             // Player has matched the bet, but maybe action isn't closed yet (e.g., they were the initial bettor/raiser)
             // This is handled by the roundOver check. If round isn't over, someone else must need to act.
             console.log(`[FindNext V2] P${potentialPlayer.id} (${potentialPlayer.label}) has matched bet ${finalMaxContributionOnStreet}, checking next.`);
             continue;
        } else if (!bettingOpened) {
            // Betting wasn't opened (check-around). The next player in line (who hasn't folded) acts.
             console.log(`[FindNext V2] Betting not open. Next player in line is P${potentialPlayer.id} (${potentialPlayer.label})`);
             // Bets state remains unchanged (0 for the street)
             return { 
               nextPlayerId: potentialPlayer.id, 
               nextStreet: currentStreetState, 
               nextBets: currentBets, 
               nextMinBet: 0,
               streetContributions 
            };
        }
    }

    // --- Fallback / Error ---
    console.warn(`[calculateNextPlayer V2] Failed to find next player to act on ${currentStreetState} after P${actingPlayerId}. Loop completed. Players in hand: ${orderedPlayersInHand.map(p=>`P${p.id}(${p.label})`)}. Contributions:`, streetContributions, `Max: ${finalMaxContributionOnStreet}`);
    // This implies the round should have ended. Return null.
    return { 
      nextPlayerId: null, 
      nextStreet: currentStreetState, 
      nextBets: { ...currentBets, [currentStreetState]: finalMaxContributionOnStreet }, 
      nextMinBet: 0,
      streetContributions
    }; // Assume round end

  }, [smallBlind, bigBlind]); // Dependencies: REMOVED getOrderedPlayers, added blinds

  // --- Set Hero Target Position (Index 5 = Middle-Left area) ---
  const heroTargetVisualIndex = 5;

  // --- Define handleAction as a standard function --- 
  const handleAction = (seatId: number, actionType: ActionType) => {
    if (!isSetupComplete || seatId !== currentPlayerId) {
      console.warn(`Action blocked: Setup complete=${isSetupComplete}, Acting Seat=${seatId}, Current Player=${currentPlayerId}`);
      return;
    }
    console.log(`[handleAction ENTRY] Seat ${seatId} (${currentStreet}), Action: ${actionType}, CurrentPlayer: ${currentPlayerId}, MinBetState: ${minBetAmount}`);

    if (actionType === 'bet' || actionType === 'raise') {
      console.log(`[handleAction] Detected ${actionType}. Preparing modal...`);
      const effectiveStack = stackSizes[seatId] ?? 0;
      setCurrentStackForBetInput(effectiveStack);
      const currentMin = minBetAmount;
      const finalMinBet = Math.min(effectiveStack, currentMin);
      console.log(`[handleAction] Calculated for modal: effectiveStack=${effectiveStack}, currentMinState=${currentMin}, finalMinBet=${finalMinBet}`);
      setBetInputActionTarget({ seatId, actionType });
      // Decide if setMinBetAmount should happen here or only in calculateNextPlayer
      // For now, let's keep it, but it might need adjustment.
      setMinBetAmount(finalMinBet); 
      setShowBetInput(true);
      console.log(`[handleAction] Setting modal state: showBetInput=true, target={seatId:${seatId}, actionType:${actionType}}`);
      return;
    }

    console.log(`[handleAction] Handling non-bet/raise action: ${actionType}`);
    const newAction: Action = { type: actionType };
    if (actionType === 'call') {
      newAction.amount = bets[currentStreet] ?? 0;
    }

    // Take a snapshot of the current player street contributions before the action
    const prevPlayerStreetContributions = { ...playerStreetContributions };

    const updatedActions = { ...playerActions };
    if (!updatedActions[seatId]) updatedActions[seatId] = {};
    if (!updatedActions[seatId]?.[currentStreet]) updatedActions[seatId]![currentStreet] = [];
    updatedActions[seatId]![currentStreet]!.push(newAction);
    setPlayerActions(updatedActions);

    let updatedFoldedIds = foldedPlayerIds;
    if (actionType === 'fold') {
      updatedFoldedIds = new Set(foldedPlayerIds).add(seatId);
      setFoldedPlayerIds(updatedFoldedIds);
    }

    const { nextPlayerId, nextStreet, nextBets, nextMinBet, streetContributions } = calculateNextPlayerToAct(
      currentPlayerId, newAction, currentStreet, seats9Max,
      updatedActions, bets, updatedFoldedIds
    );

    // Update pot based on new contributions
    let newPotAmount = pot;
    // Calculate new money added to the pot
    Object.entries(streetContributions).forEach(([playerId, contribution]) => {
      const prevContribution = prevPlayerStreetContributions[Number(playerId)] || 0;
      const newMoneyAdded = contribution - prevContribution;
      if (newMoneyAdded > 0) {
        newPotAmount += newMoneyAdded;
      }
    });

    // Check for street change to reset player contributions
    if (nextStreet !== currentStreet) {
      // If street changes, visually reset all chips on the table
      // IMPORTANT: We only reset visual chips on the table, but the pot continues to accumulate
      // The pot value in newPotAmount already includes all previous streets' contributions
      setPlayerStreetContributions({});
    } else {
      // Update player street contributions to reflect the new state
      setPlayerStreetContributions(streetContributions);
    }

    // Update state based on calculation
    setPot(newPotAmount);
    setNextPlayerToActId(nextPlayerId);
    setCurrentPlayerId(null); 
    setCurrentStreet(nextStreet);
    setBets(nextBets); // Update bets state
    setMinBetAmount(nextMinBet); // Update min bet for the next action
    console.log(`<<< handleAction END (non-bet/raise): Set currentPlayerId to ${nextPlayerId}, street to ${nextStreet}, nextMinBet to ${nextMinBet} >>>`);
  };
  // --- End handleAction definition --- 

  // --- Other Callbacks (useCallback is fine here as dependencies are simpler) ---
  const handleSetHeroPosition = useCallback((position: PlaygroundPosition | null) => {
    setSelectedHeroPosition(position); // Keep track of the selection

    if (position) {
      const heroSeat = seats9Max.find(seat => seat.label === position);
      if (heroSeat) {
        console.log(`Auto-confirming: Hero Position=${position}, Setting Hero ID=${heroSeat.id}`);
        setHeroSeatId(heroSeat.id);
        setIsSetupComplete(true); // Mark setup as complete once a valid hero is set
      } else {
        console.error(`Could not find seat ID for position: ${position}`);
        setHeroSeatId(null);
        setIsSetupComplete(false); // Invalidate setup if position doesn't match a seat
      }
    } else {
      // Position is null (e.g., user selected "-- Select --")
      console.log("Hero position deselected.");
      setHeroSeatId(null);
      setIsSetupComplete(false); // Invalidate setup if no hero is selected
    }
  }, []);

  const handleSmallBlindChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value; 
    // Allow empty string temporarily, otherwise parse as number (default to 0 if parsing fails)
    setSmallBlind(value === '' ? 0 : parseInt(value, 10) || 0); 
  }, []);

  const handleBigBlindChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBigBlind(value === '' ? 0 : parseInt(value, 10) || 0);
  }, []);

  // --- Handler for Bet Amount Input Confirmation ---
  const handleBetConfirm = useCallback((amount: number) => {
    console.log('[handleBetConfirm ENTRY]', { amount, betInputActionTarget });

    if (!betInputActionTarget) {
       console.error('[handleBetConfirm] Error: betInputActionTarget is null!');
       setShowBetInput(false); 
       setBetInputActionTarget(null);
       setCurrentStackForBetInput(0);
       return;
    }

    const { seatId, actionType } = betInputActionTarget;
    console.log(`Bet confirmed: P${seatId}, Action: ${actionType}, Amount: ${amount}`);

    const newAction: Action = { type: actionType, amount: amount };
    // TODO: Check if amount equals stack for isAllIn flag

    // Take a snapshot of the current player street contributions before the action
    const prevPlayerStreetContributions = { ...playerStreetContributions };

    // --- Combine logic from handleAction's end ---
    // Update player actions state
    const updatedActions = { ...playerActions };
    if (!updatedActions[seatId]) {
      updatedActions[seatId] = {};
    }
    if (!updatedActions[seatId]?.[currentStreet]) {
      updatedActions[seatId]![currentStreet] = [];
    }
    updatedActions[seatId]![currentStreet]!.push(newAction);
    setPlayerActions(updatedActions);

    // Folded state doesn't change on bet/raise
    const updatedFoldedIds = foldedPlayerIds;

    // Calculate next player / street
    const { nextPlayerId, nextStreet, nextBets, nextMinBet, streetContributions } = calculateNextPlayerToAct(
      currentPlayerId, // Use current player before update
      newAction,
      currentStreet,
      seats9Max,
      updatedActions, // Pass the *newly updated* actions
      bets,           // Pass the bets *before* this action
      updatedFoldedIds
    );

    // Update pot based on new contributions
    let newPotAmount = pot;
    // Calculate new money added to the pot
    Object.entries(streetContributions).forEach(([playerId, contribution]) => {
      const prevContribution = prevPlayerStreetContributions[Number(playerId)] || 0;
      const newMoneyAdded = contribution - prevContribution;
      if (newMoneyAdded > 0) {
        newPotAmount += newMoneyAdded;
      }
    });

    // Check for street change to reset player contributions
    if (nextStreet !== currentStreet) {
      // If street changes, visually reset all chips on the table
      // IMPORTANT: We only reset visual chips on the table, but the pot continues to accumulate
      // The pot value in newPotAmount already includes all previous streets' contributions
      setPlayerStreetContributions({});
    } else {
      // Update player street contributions to reflect the new state
      setPlayerStreetContributions(streetContributions);
    }

    // Update state based on calculation
    setPot(newPotAmount);
    setNextPlayerToActId(nextPlayerId);
    setCurrentPlayerId(null); 
    setCurrentStreet(nextStreet);
    setBets(nextBets); // Update bets state
    setMinBetAmount(nextMinBet); // Update min bet for the next action

    // --- Reset Bet Input State --- 
    console.log('[handleBetConfirm] About to set showBetInput to false.');
    setShowBetInput(false);
    setBetInputActionTarget(null);
    setCurrentStackForBetInput(0);
    console.log(`<<< handleBetConfirm END: Set currentPlayerId to ${nextPlayerId}, street to ${nextStreet}, nextMinBet to ${nextMinBet} >>>`);

  }, [betInputActionTarget, playerActions, currentStreet, bets, foldedPlayerIds, calculateNextPlayerToAct, currentPlayerId, pot, playerStreetContributions]); // Dependencies

  // --- Handler to Cancel Bet Input ---
  const handleBetCancel = useCallback(() => {
      console.log('[handleBetCancel ENTRY] About to set showBetInput to false.');
      setShowBetInput(false);
      setBetInputActionTarget(null);
      setCurrentStackForBetInput(0);
      console.log("Bet input cancelled.");
  }, []); // Dependencies should be empty as only setters are used

  const handleHeroCard1Click = useCallback(() => {
    if (!isSetupComplete) return;
    setCardPickerTarget('holeCard1');
    setShowCardPicker(true);
  }, [isSetupComplete]);

  const handleHeroCard2Click = useCallback(() => {
    if (!isSetupComplete) return;
    setCardPickerTarget('holeCard2');
    setShowCardPicker(true);
  }, [isSetupComplete]);

  // Add handlers for community cards
  const handleFlopCard1Click = useCallback(() => {
    if (!isSetupComplete) return;
    setCardPickerTarget('flopCard1');
    setShowCardPicker(true);
  }, [isSetupComplete]);

  const handleFlopCard2Click = useCallback(() => {
    if (!isSetupComplete) return;
    setCardPickerTarget('flopCard2');
    setShowCardPicker(true);
  }, [isSetupComplete]);

  const handleFlopCard3Click = useCallback(() => {
    if (!isSetupComplete) return;
    setCardPickerTarget('flopCard3');
    setShowCardPicker(true);
  }, [isSetupComplete]);

  // New handler to handle all flop cards at once
  const handleFlopCardsClick = useCallback(() => {
    if (!isSetupComplete) return;
    
    // If we're on the flop and none of the flop cards are set, open the card picker for the first card
    if (currentStreet === 'flop' && 
        !communityCards.flopCard1 && 
        !communityCards.flopCard2 && 
        !communityCards.flopCard3) {
      setCardPickerTarget('flopCard1');
      setShowCardPicker(true);
    }
  }, [isSetupComplete, currentStreet, communityCards]);

  const handleTurnCardClick = useCallback(() => {
    if (!isSetupComplete) return;
    setCardPickerTarget('turnCard');
    setShowCardPicker(true);
  }, [isSetupComplete]);

  const handleRiverCardClick = useCallback(() => {
    if (!isSetupComplete) return;
    setCardPickerTarget('riverCard');
    setShowCardPicker(true);
  }, [isSetupComplete]);

  const handleHeroCardSelect = useCallback((card: string) => {
    if (cardPickerTarget) {
      if (cardPickerTarget === 'holeCard1' || cardPickerTarget === 'holeCard2') {
        setHeroCards(prevCards => ({ ...prevCards, [cardPickerTarget]: card }));
      } else {
        // Handle community cards (flop, turn, river)
        let updatedCommunityCards = { ...communityCards, [cardPickerTarget]: card };
        setCommunityCards(updatedCommunityCards);
        
        // Auto-advance to the next card if on flop and the current card was set
        if (cardPickerTarget === 'flopCard1' && !communityCards.flopCard2) {
          setCardPickerTarget('flopCard2');
          return; // Don't close the card picker yet
        } else if (cardPickerTarget === 'flopCard2' && !communityCards.flopCard3) {
          setCardPickerTarget('flopCard3');
          return; // Don't close the card picker yet
        } else if (cardPickerTarget === 'flopCard3' || cardPickerTarget === 'turnCard' || cardPickerTarget === 'riverCard') {
          // When setting the final flop card, turn card, or river card, make sure to update the current player
          // to allow betting actions
          
          // Check if we've just completed all flop cards
          const isFlop = cardPickerTarget === 'flopCard3' && card && updatedCommunityCards.flopCard1 && updatedCommunityCards.flopCard2;
          
          // Check if we've just set the turn card
          const isTurn = cardPickerTarget === 'turnCard' && card;
          
          // Check if we've just set the river card 
          const isRiver = cardPickerTarget === 'riverCard' && card;
          
          if (isFlop || isTurn || isRiver) {
            console.log(`[handleHeroCardSelect] Completed setting ${cardPickerTarget}, activating next player`);
            // If the next player to act is already set, make them the current player
            if (nextPlayerToActId !== null) {
              setCurrentPlayerId(nextPlayerToActId);
              setNextPlayerToActId(null);
            }
          }
        }
      }
    }
    setShowCardPicker(false);
    setCardPickerTarget(null);
  }, [cardPickerTarget, communityCards, nextPlayerToActId]);

  const handleStackChange = useCallback((seatId: number, value: string) => {
    const numericValue = parseInt(value, 10);
    // Allow empty input (treat as 0), otherwise use parsed number or 0 if NaN
    const finalValue = value === '' ? 0 : (isNaN(numericValue) ? 0 : numericValue); 
    setStackSizes(prev => ({ ...prev, [seatId]: finalValue }));
  }, []);

  // --- Effects ---
  useEffect(() => {
    const defaultStack = 100 * bigBlind;
    const initialStacks: Record<number, number> = {};
    seats9Max.forEach(seat => {
      // Only update if the stack hasn't been manually set yet, or always reset?
      // Let's reset all to default when BB changes for now.
      initialStacks[seat.id] = defaultStack;
    });
    setStackSizes(initialStacks);
    // --- Calculate initial pot when BB changes (and setup is complete) --- 
    if (isSetupComplete) { 
      setPot(smallBlind + bigBlind);
    }
  }, [bigBlind, isSetupComplete, smallBlind]); // Add isSetupComplete and smallBlind dependencies

  // --- Add effect to auto-prompt Turn card selection when transitioning to turn street ---
  useEffect(() => {
    // Check if we're on the turn street and the turn card isn't set yet
    if (currentStreet === 'turn' && !communityCards.turnCard) {
      // Automatically open the card picker for the turn card
      setCardPickerTarget('turnCard');
      setShowCardPicker(true);
    }
    // Check if we're on the river street and the river card isn't set yet
    else if (currentStreet === 'river' && !communityCards.riverCard) {
      // Automatically open the card picker for the river card
      setCardPickerTarget('riverCard');
      setShowCardPicker(true);
    }
  }, [currentStreet, communityCards.turnCard, communityCards.riverCard]);

  useEffect(() => {
    if (isSetupComplete) {
      setPot(smallBlind + bigBlind);
      // --- Initialize Action State on Setup Complete ---
      setCurrentStreet('preflop');
      setPlayerActions({});
      const initialFoldedIds = new Set<number>(); // Create locally
      setFoldedPlayerIds(initialFoldedIds); // Set the empty set
      setBets({ preflop: bigBlind }); // Initialize preflop bet amount
      
      // Initialize player street contributions with blinds
      const initialStreetContributions: Record<number, number> = {};
      const sbPlayer = seats9Max.find(p => p.label === 'SB');
      const bbPlayer = seats9Max.find(p => p.label === 'BB');
      if (sbPlayer) {
        initialStreetContributions[sbPlayer.id] = smallBlind;
      }
      if (bbPlayer) {
        initialStreetContributions[bbPlayer.id] = bigBlind;
      }
      setPlayerStreetContributions(initialStreetContributions);
      
      // Reset community cards
      setCommunityCards({
        flopCard1: '',
        flopCard2: '',
        flopCard3: '',
        turnCard: '',
        riverCard: ''
      });
      
      // Determine first player to act preflop
      const ordered = getOrderedPlayers('preflop', seats9Max);
      // Find first player NOT in the initialFoldedIds (which is empty)
      const firstPlayer = ordered.find(p => !initialFoldedIds.has(p.id));
      setCurrentPlayerId(firstPlayer ? firstPlayer.id : null);
      console.log("Setup complete, action state initialized. First player:", firstPlayer?.id, `(${firstPlayer?.label})`);
    } else {
      setPot(0); // Reset pot if setup becomes incomplete
      setCurrentPlayerId(null); // Reset current player
      setPlayerActions({});
      setFoldedPlayerIds(new Set());
      setBets({});
      setCurrentStreet('preflop'); // Reset street
      setPlayerStreetContributions({}); // Reset player street contributions
      // Reset community cards
      setCommunityCards({
        flopCard1: '',
        flopCard2: '',
        flopCard3: '',
        turnCard: '',
        riverCard: ''
      });
    }
  }, [isSetupComplete, smallBlind, bigBlind]); // REMOVED seats9Max dependency (now stable)

  // --- NEW: Handler for clicking on a seat to advance turn --- 
  const handleSeatClick = useCallback((seatId: number) => {
    console.log(`[handleSeatClick] Clicked seat: ${seatId}, Next expected: ${nextPlayerToActId}`);
    if (seatId === nextPlayerToActId) {
      console.log(`   -> Match! Setting current player to ${seatId}`);
      setCurrentPlayerId(seatId);
      setNextPlayerToActId(null); // Clear the next player indicator
    } else {
      console.log(`   -> No match or no next player expected.`);
    }
  }, [nextPlayerToActId]); // Dependency: nextPlayerToActId



  // --- Reconstruct currentBetToCall from original logic ---
  const currentBetToCall = useMemo(() => {
    if (currentPlayerId === null) return 0;
    const playerCurrentStreetActions = playerActions[currentPlayerId]?.[currentStreet] || [];
    const playerContributionThisStreet = playerCurrentStreetActions.reduce((sum, action) => sum + (action.amount || 0), 0);
    
    // Pre-flop considerations for blinds
    let streetMaxContribution = 0;
    if (currentStreet === 'preflop') {
      streetMaxContribution = bigBlind; // BB is the initial amount to call
      // Check if there were raises beyond BB
      seats9Max.forEach(seat => {
        if (seat.id !== currentPlayerId) {
          const seatActions = playerActions[seat.id]?.[currentStreet] || [];
          const seatContribution = seatActions.reduce((sum, action) => sum + (action.amount || 0), 0);
          streetMaxContribution = Math.max(streetMaxContribution, seatContribution);
        }
      });
    } else {
      // Post-flop: find max contribution from other players on this street
      seats9Max.forEach(seat => {
        if (seat.id !== currentPlayerId) {
          const seatActions = playerActions[seat.id]?.[currentStreet] || [];
          const seatContribution = seatActions.reduce((sum, action) => sum + (action.amount || 0), 0);
          streetMaxContribution = Math.max(streetMaxContribution, seatContribution);
        }
      });
    }
    return Math.max(0, streetMaxContribution - playerContributionThisStreet);
  }, [currentPlayerId, playerActions, currentStreet, bigBlind, seats9Max]);

  return (
    <div className="playground-container">
      {!isSetupComplete && (
         <div style={{ color: 'white', marginBottom: '10px', fontWeight: 'bold' }}>
           Please select Hero's position in the Setup tab below and confirm.
         </div>
      )}
      
      <div className="table-wrapper">
        <Table 
          seatCount={9}
          seats={seats9Max}
          heroSeatId={heroSeatId} // Pass the confirmed hero ID
          heroTargetVisualIndex={heroTargetVisualIndex} // Pass the fixed target visual index
          onAction={handleAction} 
          isSetupComplete={isSetupComplete}
          // REMOVED: sbSeatId, bbSeatId - PokerVisual calculates these
          // --- Pass SB/BB values down --- 
          smallBlind={smallBlind}
          bigBlind={bigBlind}
          // --- Pass hero card state/handlers down ---
          heroCards={heroCards}
          onHeroCard1Click={handleHeroCard1Click}
          onHeroCard2Click={handleHeroCard2Click}
          // --- Pass community cards state/handlers ---
          communityCards={communityCards}
          onFlopCard1Click={handleFlopCard1Click}
          onFlopCard2Click={handleFlopCard2Click}
          onFlopCard3Click={handleFlopCard3Click}
          onFlopCardsClick={handleFlopCardsClick}
          onTurnCardClick={handleTurnCardClick}
          onRiverCardClick={handleRiverCardClick}
          // --- Pass stack sizes and handler ---
          stackSizes={stackSizes}
          onStackChange={handleStackChange}
          // --- Pass pot size --- 
          pot={pot}
          // --- Pass action state ---
          currentPlayerId={currentPlayerId}
          // <<< ADDED: Pass next player ID and click handler >>>
          nextPlayerToActId={nextPlayerToActId}
          onSeatClick={handleSeatClick}
          foldedPlayerIds={foldedPlayerIds}
          // --- Pass current bet amount TO CALL ---
          currentBetToCall={currentBetToCall}
          // --- Pass current street ---
          currentStreet={currentStreet}
          isDebugSwivelModeActive={isDebugSwivelMode}
          playerStreetContributions={playerStreetContributions}
        />
      </div>
      <PlaygroundSettingsBar 
        seats={seats9Max}
        selectedHeroPosition={selectedHeroPosition}
        onSetHeroPosition={(position: PlaygroundPosition | null) => {
          setSelectedHeroPosition(position);
          const heroSeat = seats9Max.find(s => s.label === position);
          setHeroSeatId(heroSeat ? heroSeat.id : null);
        }}
        onSetupStatusChange={(isCompleted: boolean) => {
          console.log("Setup Status Changed via onSetupStatusChange, Completed:", isCompleted);
          setIsSetupComplete(isCompleted);
          if (isCompleted) {
            const initialNextPlayerInfo = calculateNextPlayerToAct(null, null, 'preflop', seats9Max, {}, {}, new Set());
            setCurrentPlayerId(initialNextPlayerInfo.nextPlayerId);
            setNextPlayerToActId(initialNextPlayerInfo.nextPlayerId);
            setBets(initialNextPlayerInfo.nextBets);
            setMinBetAmount(initialNextPlayerInfo.nextMinBet);
            console.log("[Setup Complete] Initial Player:", initialNextPlayerInfo.nextPlayerId, "Initial Bets:", initialNextPlayerInfo.nextBets, "Initial MinBet:", initialNextPlayerInfo.nextMinBet);
            
            const defaultStack = bigBlind * 100;
            const initialStacks: Record<number, number> = {};
            const sbPlayer = seats9Max.find(p => p.label === 'SB');
            const bbPlayer = seats9Max.find(p => p.label === 'BB');
            
            seats9Max.forEach(seat => {
              // Subtract blinds from initial stacks for SB and BB
              if (sbPlayer && seat.id === sbPlayer.id) {
                initialStacks[seat.id] = defaultStack - smallBlind;
              } else if (bbPlayer && seat.id === bbPlayer.id) {
                initialStacks[seat.id] = defaultStack - bigBlind;
              } else {
                initialStacks[seat.id] = defaultStack;
              }
            });
            setStackSizes(initialStacks);
            
            // Initialize player street contributions with blinds
            const initialStreetContributions: Record<number, number> = {};
            if (sbPlayer) {
              initialStreetContributions[sbPlayer.id] = smallBlind;
            }
            if (bbPlayer) {
              initialStreetContributions[bbPlayer.id] = bigBlind;
            }
            setPlayerStreetContributions(initialStreetContributions);
            
            // Initialize pot with blinds
            setPot(smallBlind + bigBlind);

          } else {
            setSelectedHeroPosition(null);
            setHeroSeatId(null);
            setHeroCards({ holeCard1: '', holeCard2: '' });
            setPlayerActions({});
            setCurrentPlayerId(null);
            setNextPlayerToActId(null);
            setFoldedPlayerIds(new Set());
            setPot(0);
            setBets({});
            setCurrentStreet('preflop');
            setStackSizes({});
          }
        }}
        isSetupComplete={isSetupComplete}
        smallBlind={smallBlind}
        bigBlind={bigBlind}
        onSmallBlindChange={(event: React.ChangeEvent<HTMLInputElement>) => setSmallBlind(Number(event.target.value) || 0)}
        onBigBlindChange={(event: React.ChangeEvent<HTMLInputElement>) => setBigBlind(Number(event.target.value) || 0)}
      />

      {/* Render Card Picker Conditionally */}
      {showCardPicker && (
        <CardPicker 
          show={showCardPicker}
          onSelect={handleHeroCardSelect} 
          onClose={() => setShowCardPicker(false)}
          hand={{ 
            holeCard1: heroCards.holeCard1, 
            holeCard2: heroCards.holeCard2, 
            flopCard1: communityCards.flopCard1,
            flopCard2: communityCards.flopCard2,
            flopCard3: communityCards.flopCard3,
            turnCard: communityCards.turnCard,
            riverCard: communityCards.riverCard
          }}
        />
      )}

      {/* Render Bet Amount Input Conditionally - Simplified */}
      {showBetInput && betInputActionTarget && currentStackForBetInput !== null && (
         <BetAmountInput
            show={showBetInput}
            onConfirm={handleBetConfirm}
            onCancel={handleBetCancel}
            minBet={minBetAmount}
            maxBet={currentStackForBetInput}
            // Explicitly assert the type
            actionType={betInputActionTarget.actionType as 'bet' | 'raise'} 
            currentBetToCall={currentBetToCall}
        />
      )}
    </div>
  );
};

export default PlaygroundPage; 

// Helper component to adapt CardPicker's props if needed, or adjust CardPicker itself
// For now, passing a simplified 'usedCards' object.
// We need to define the CardPickerProps interface here or import it.

// --- Card Picker Component Interface (Simplified version for PlaygroundPage) ---
// Assuming CardPicker can accept a simpler `usedCards` prop instead of the full `Hand`.
// If not, CardPicker needs adjustment or we define the full Hand type here.
// REMOVE local definition, importing from BuddyPage
/*
interface CardPickerProps {
  show: boolean;
  onSelect: (card: string) => void;
  onClose: () => void;
  usedCards: { [key: string]: string }; // Simplified: object of used cards
}
*/ 